import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaBullhorn, FaChartLine, FaUsers, FaRocket } from 'react-icons/fa';
import Section from '../../components/common/Section';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';

const PageContainer = styled(motion.div)`
  padding-top: 100px;
`;

const HeroSection = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.space['3xl']} 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url('/assets/images/pattern.svg');
    opacity: 0.03;
    pointer-events: none;
  }
`;

const HeroTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes['5xl']};
  margin-bottom: ${({ theme }) => theme.space.lg};
  position: relative;
  display: inline-block;
  
  span {
    color: ${({ theme }) => theme.colors.accent};
    font-family: ${({ theme }) => theme.fonts.accent};
    font-style: italic;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes['4xl']};
  }
`;

const HeroDescription = styled.p`
  max-width: 800px;
  margin: 0 auto ${({ theme }) => theme.space.xl};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.grey[700]};
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.space.xl};
  margin-top: ${({ theme }) => theme.space['2xl']};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const ServiceTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  margin-bottom: ${({ theme }) => theme.space.md};
  color: ${({ theme }) => theme.colors.primary};
`;

const ServiceDescription = styled.p`
  margin-bottom: ${({ theme }) => theme.space.lg};
  line-height: 1.7;
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.gradient.accent};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.space.lg};
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  color: ${({ theme }) => theme.colors.dark};
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
`;

const ProcessSection = styled.div`
  margin-top: ${({ theme }) => theme.space['3xl']};
`;

const ProcessTitle = styled.h2`
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  margin-bottom: ${({ theme }) => theme.space['2xl']};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const ProcessSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.space.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ProcessStep = styled.div`
  text-align: center;
  position: relative;
  
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 40px;
    right: -50%;
    width: 100%;
    height: 2px;
    background: ${({ theme }) => theme.colors.gradient.accent};
    z-index: -1;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
      display: none;
    }
  }
`;

const StepNumber = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.gradient.primary};
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${({ theme }) => theme.space.md};
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
`;

const StepTitle = styled.h4`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  margin-bottom: ${({ theme }) => theme.space.sm};
  color: ${({ theme }) => theme.colors.primary};
`;

const StepDescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.grey[700]};
`;

const CTASection = styled.div`
  text-align: center;
  margin-top: ${({ theme }) => theme.space['3xl']};
  padding: ${({ theme }) => theme.space['2xl']} ${({ theme }) => theme.space.xl};
  background: ${({ theme }) => theme.colors.gradient.primary};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  color: ${({ theme }) => theme.colors.white};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url('/assets/images/pattern.svg');
    opacity: 0.05;
    pointer-events: none;
  }
`;

const CTATitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  margin-bottom: ${({ theme }) => theme.space.lg};
`;

const CTADescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  max-width: 800px;
  margin: 0 auto ${({ theme }) => theme.space.xl};
  line-height: 1.7;
`;

const DigitalMarketing: React.FC = () => {
  return (
    <PageContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Section>
        <HeroSection>
          <HeroTitle>Digital <span>Marketing</span> Services</HeroTitle>
          <HeroDescription>
            Elevate your brand with our comprehensive digital marketing strategies designed to increase visibility, drive engagement, and convert leads into loyal customers.
          </HeroDescription>
          <Button variant="accent" size="large">Schedule a Consultation</Button>
        </HeroSection>
        
        <ServicesGrid>
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaBullhorn />
            </IconWrapper>
            <ServiceTitle>Search Engine Optimization</ServiceTitle>
            <ServiceDescription>
              Our SEO strategies are designed to improve your website's visibility in search engine results, driving organic traffic and increasing your online presence. We use the latest techniques and tools to ensure your website ranks higher for relevant keywords.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
          
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaChartLine />
            </IconWrapper>
            <ServiceTitle>Pay-Per-Click Advertising</ServiceTitle>
            <ServiceDescription>
              Maximize your ROI with our targeted PPC campaigns. We create and manage ads that appear at the top of search results, driving immediate traffic to your website and generating leads quickly. Our team continuously optimizes your campaigns for the best results.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
          
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaUsers />
            </IconWrapper>
            <ServiceTitle>Social Media Marketing</ServiceTitle>
            <ServiceDescription>
              Connect with your audience where they spend their time. Our social media marketing services help you build a strong presence across platforms, engage with your community, and create compelling content that resonates with your target audience.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
          
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaRocket />
            </IconWrapper>
            <ServiceTitle>Content Marketing</ServiceTitle>
            <ServiceDescription>
              Tell your brand's story with compelling content that educates, entertains, and inspires. Our content marketing strategies include blog posts, videos, infographics, and more, all designed to establish your authority and drive engagement.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
        </ServicesGrid>
        
        <ProcessSection>
          <ProcessTitle>Our Digital Marketing Process</ProcessTitle>
          <ProcessSteps>
            <ProcessStep>
              <StepNumber>1</StepNumber>
              <StepTitle>Research & Analysis</StepTitle>
              <StepDescription>We analyze your industry, competitors, and target audience to develop a tailored strategy.</StepDescription>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>2</StepNumber>
              <StepTitle>Strategy Development</StepTitle>
              <StepDescription>We create a comprehensive marketing plan aligned with your business goals and objectives.</StepDescription>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>3</StepNumber>
              <StepTitle>Implementation</StepTitle>
              <StepDescription>Our team executes the strategy across all relevant channels and platforms.</StepDescription>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>4</StepNumber>
              <StepTitle>Monitoring & Optimization</StepTitle>
              <StepDescription>We continuously track performance and refine our approach for maximum results.</StepDescription>
            </ProcessStep>
          </ProcessSteps>
        </ProcessSection>
        
        <CTASection>
          <CTATitle>Ready to Transform Your Digital Presence?</CTATitle>
          <CTADescription>
            Let our team of digital marketing experts help you reach your target audience, drive engagement, and achieve your business goals. Schedule a consultation today to learn how our tailored strategies can work for you.
          </CTADescription>
          <Button variant="accent" size="large">Get Started</Button>
        </CTASection>
      </Section>
    </PageContainer>
  );
};

export default DigitalMarketing;
